/**
 * Global Supabase Auth Interceptor
 *
 * Wraps the global `fetch` to detect JWT-related failures (401, 403 with
 * PGRST301) on Supabase API calls and forces a clean sign-out with a toast.
 *
 * Scoped narrowly to the project's Supabase URL to avoid interfering with
 * other network calls. Uses a debounce flag so parallel failing requests
 * produce only one sign-out + toast.
 */

import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const JWT_ERROR_CODES = ["PGRST301", "PGRST302"];
const DEBOUNCE_MS = 2000;

let isSigningOut = false;

function triggerSessionExpiredSignOut() {
  if (isSigningOut) return;
  isSigningOut = true;

  toast({
    title: "Session expired",
    description: "Please sign in again",
    variant: "destructive",
  });

  supabase.auth.signOut().finally(() => {
    setTimeout(() => {
      isSigningOut = false;
    }, DEBOUNCE_MS);
  });
}

/**
 * Installs a fetch wrapper that intercepts Supabase API responses.
 * Returns a cleanup function that restores the original fetch.
 */
export function installAuthInterceptor(): () => void {
  const originalFetch = window.fetch;

  window.fetch = async function interceptedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const response = await originalFetch.call(window, input, init);

    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;

    // Read lazily so that vi.stubEnv works in tests (module-level capture would freeze it)
    const supabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl || !url.startsWith(supabaseUrl)) return response;
    // Skip auth endpoints — the auth listener handles those
    if (url.includes("/auth/")) return response;

    if (response.status === 401) {
      triggerSessionExpiredSignOut();
    } else if (response.status === 403) {
      // Clone to read body without consuming the original
      try {
        const clone = response.clone();
        const body = await clone.json();
        if (body?.code && JWT_ERROR_CODES.includes(body.code)) {
          triggerSessionExpiredSignOut();
        }
      } catch {
        // Body not JSON — ignore
      }
    }

    return response;
  };

  return () => {
    window.fetch = originalFetch;
  };
}
